import React, { useState, useEffect } from 'react'
import { useWebSocket } from '../contexts/WebSocketContext'
import { WebSocketStatus } from '../types/websocket.types'
import { Button, Card, CardContent, Typography, Box, TextField, CircularProgress, Alert } from '@mui/material'
import { useAuth } from 'react-oidc-context'

const WebSocketTestPanel: React.FC = () => {
  const auth = useAuth()
  const {
    connect,
    disconnect,
    subscribeToDevice,
    status,
    messages,
    isConnected,
    clearMessages,
    authData
  } = useWebSocket()

  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  const [url, setUrl] = useState(`${window._env_?.REACT_APP_WEBSOCKET_HOST ?? ''}/udp/tiq/ws/dashboard`)
  const [tenantId, setTenantId] = useState(authData.tenantId ?? '')
  const [username, setUsername] = useState(authData.username ?? '')
  const [deviceId, setDeviceId] = useState('')
  const [isSubscribing, setIsSubscribing] = useState(false)
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null)

  // Update values when auth changes
  useEffect(() => {
    if (authData.tenantId !== undefined && authData.tenantId !== '') {
      setTenantId(authData.tenantId)
    }
    if (authData.username !== undefined && authData.username !== '') {
      setUsername(authData.username)
    }
  }, [authData])

  const handleConnect = (): void => {
    // Pass the current values, or let the context use auth data as fallback
    connect(url, tenantId, username)
  }

  const handleDisconnect = (): void => {
    disconnect()
  }

  const handleSubscribe = async (): Promise<void> => {
    if (!isConnected) {
      setSubscriptionError('WebSocket not connected')
      return
    }

    setIsSubscribing(true)
    setSubscriptionError(null)

    try {
      const response = await subscribeToDevice(deviceId)
      console.log('Subscription successful:', response)
    } catch (error) {
      console.error('Subscription failed:', error)
      setSubscriptionError(error instanceof Error ? error.message : 'Subscription failed')
    } finally {
      setIsSubscribing(false)
    }
  }

  const getStatusColor = (status: WebSocketStatus): 'success' | 'warning' | 'error' | 'info' => {
    switch (status) {
      case WebSocketStatus.CONNECTED:
        return 'success'
      case WebSocketStatus.CONNECTING:
        return 'warning'
      case WebSocketStatus.ERROR:
        return 'error'
      case WebSocketStatus.DISCONNECTED:
      default:
        return 'info'
    }
  }

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h5" gutterBottom>
        WebSocket Test Panel
      </Typography>

      <Card sx={{ marginBottom: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Authentication Status
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
            <Alert severity={auth.isAuthenticated ? 'success' : 'warning'} sx={{ padding: '0 8px' }}>
              {auth.isAuthenticated ? 'Authenticated' : 'Not Authenticated'}
            </Alert>
            {auth.isAuthenticated && (
              <Typography variant="body2" color="text.secondary">
                User: {auth.user?.profile?.name as string} | Tenant: {auth.user?.profile?.customer as string}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ marginBottom: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Connection Settings
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="WebSocket URL"
              value={url}
              onChange={(e) => { setUrl(e.target.value) }}
              fullWidth
              size="small"
            />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Tenant ID"
                value={tenantId}
                onChange={(e) => { setTenantId(e.target.value) }}
                size="small"
              />
              <TextField
                label="Username"
                value={username}
                onChange={(e) => { setUsername(e.target.value) }}
                size="small"
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ marginBottom: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Connection Status
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
            <Typography variant="body1">
              Status:
            </Typography>
            <Alert severity={getStatusColor(status)} sx={{ padding: '0 8px' }}>
              {status.toUpperCase()}
            </Alert>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              onClick={handleConnect}
              disabled={status === WebSocketStatus.CONNECTING}
              color="primary"
            >
              {status === WebSocketStatus.CONNECTING ? <CircularProgress size={20} /> : 'Connect'}
            </Button>

            <Button
              variant="outlined"
              onClick={handleDisconnect}
              disabled={!isConnected}
              color="secondary"
            >
              Disconnect
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ marginBottom: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Device Subscription
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
            <TextField
              label="Device ID"
              value={deviceId}
              onChange={(e) => { setDeviceId(e.target.value) }}
              size="small"
              fullWidth
            />

            <Button
              variant="contained"
              onClick={() => { void handleSubscribe() }}
              disabled={!isConnected || isSubscribing}
              color="primary"
            >
              {isSubscribing ? <CircularProgress size={20} /> : 'Subscribe'}
            </Button>
          </Box>

          {subscriptionError != null && (
            <Alert severity="error" sx={{ marginTop: 1 }}>
              {subscriptionError}
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            <Typography variant="h6">
              Messages ({messages.length})
            </Typography>
            <Button
              variant="outlined"
              onClick={clearMessages}
              size="small"
            >
              Clear
            </Button>
          </Box>

          <Box
            sx={{
              maxHeight: 400,
              overflowY: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              padding: 1,
              backgroundColor: '#f5f5f5'
            }}
          >
            {messages.length === 0
              ? (
                <Typography variant="body2" color="text.secondary">
                  No messages received yet
                </Typography>
                )
              : (
                  messages.map((message, index) => (
                    <Box key={index} sx={{ marginBottom: 1, padding: 1, backgroundColor: 'white', borderRadius: 1 }}>
                      <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(message, null, 2)}
                      </Typography>
                    </Box>
                  ))
                )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

export default WebSocketTestPanel
